import { classifyMobileWebGpuValidation } from './mobileWebGpuErrors';

const ZONE_PHASES = ['zone-download-start', 'zone-scene-complete', 'zone-unload-complete', 'zone-load-failed', 'zone-memory-blocked'] as const;
const UNITY_PHASES = [...ZONE_PHASES, 'streaming-manifest-ready', 'world-memory-blocked', 'world-download-start', 'world-download-complete', 'world-bundle-ready', 'world-scene-start', 'world-scene-complete', 'layout-download-start', 'layout-download-complete', 'layout-catalogs-start', 'layout-catalogs-complete', 'layout-spawn-start', 'layout-spawn-submitted', 'content-load-start', 'content-load-complete', 'avatar-instantiate-start', 'avatar-instantiate-complete'] as const;
const MEMORY_FIELDS = ['wasmHeapBytes', 'unityAllocatedBytes', 'unityReservedBytes'] as const;
type MemoryCheckpoint = Partial<Record<typeof MEMORY_FIELDS[number], number>>;

const STAGES = [...UNITY_PHASES, 'probe-start', 'probe-complete', 'connect-start', 'connect-ready',
  'connect-failed', 'dependencies-start', 'player-start', 'player-ready',
  'world-ready', 'player-failed', 'dependencies-failed', 'hidden', 'visible',
  'media-requested', 'media-granted', 'media-rejected', 'page-hidden',
  'runtime-error', 'resource-error', 'unhandled-rejection', 'player-progress',
  'runtime-sample', 'runtime-configured', 'client-reconnecting', 'room-transition',
  'gpu-device-lost', 'gpu-device-destroyed', 'gpu-out-of-memory', 'gpu-validation-error', 'gpu-error', 'wasm-error',
  'gpu-device-requested', 'gpu-device-ready', 'gpu-scoped-validation-error',
  'unity-runtime-ready', 'unity-post-run', 'unity-abort', 'player-init-stalled', 'mobile-world-blocked', 'app-reload-requested'] as const;
type Stage = typeof STAGES[number];

export interface LaunchAttempt {
  schemaVersion: 2;
  launchId: string;
  startedAt: string;
  voiceDisabled: boolean;
  navigationType?: string;
  browser: { userAgent: string; viewport: number[]; devicePixelRatio: number; secureContext: boolean };
  graphics?: { adapterAvailable: boolean; limits: Record<string, number>; features: string[] };
  runtime?: { unityFailureCategory?: string; firstGpuValidationCategory?: string; lastGpuValidationCategory?: string; sessionMode?: 'online' | 'offline'; quality?: string; upcRef?: string; webBuild?: string; wasmHeapBytes?: number;
    canvasWidth?: number; canvasHeight?: number; progressPercent?: number; browserRafFps?: number; maxRafGapMs?: number };
  milestones: (MemoryCheckpoint & { sequence: number; stage: Stage; elapsedMs: number; jsHeapBytes?: number })[];
}

export interface LaunchReport extends LaunchAttempt {
  previousLaunch?: LaunchAttempt;
}

const STORAGE_KEY = 'intersect-mobile-web-launch-v2';
const GRAPHICS_LIMITS = ['maxBufferSize', 'maxStorageBufferBindingSize', 'maxTextureDimension2D',
  'maxTextureArrayLayers', 'maxBindGroups', 'maxStorageBuffersPerShaderStage'];
let report: LaunchReport | undefined;
let startedAt = 0;
let sequence = 0;
let startupModule: any;
let startupWatchdog: number | undefined;
const lastFaultTimes = new Map<Stage, number>();
const FAULT_STAGES = new Set<Stage>(['runtime-error', 'resource-error', 'unhandled-rejection', 'wasm-error']);
const listeners = new Set<(snapshot: LaunchReport) => void>();
const RUNTIME_FIELDS = ['firstGpuValidationCategory', 'lastGpuValidationCategory', 'sessionMode', 'quality', 'upcRef', 'webBuild', 'wasmHeapBytes', 'canvasWidth', 'canvasHeight',
  'progressPercent', 'browserRafFps', 'maxRafGapMs'] as const;

export function subscribeMobileWebDiagnostics(listener: (snapshot: LaunchReport) => void): () => void {
  listeners.add(listener);
  const snapshot = getMobileWebReport();
  if (snapshot) listener(snapshot);
  return () => { listeners.delete(listener); };
}

function emitCheckpoint(): void {
  saveCheckpoint();
  const snapshot = getMobileWebReport();
  if (snapshot) listeners.forEach(listener => { try { listener(snapshot); } catch (_) { /* Telemetry cannot block launch. */ } });
}

function createLaunchId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    let value: number;
    try { value = crypto.getRandomValues(new Uint8Array(1))[0] & 15; }
    catch (_) { value = Math.floor(Math.random() * 16); } // Correlation ID, never an auth credential.
    return (c === 'x' ? value : (value & 3) | 8).toString(16);
  });
}

function readPreviousLaunch(): LaunchAttempt | undefined {
  try {
    const saved = window.sessionStorage.getItem(STORAGE_KEY);
    if (!saved || saved.length > 64000) return;
    const candidate = JSON.parse(saved);
    if (candidate.schemaVersion !== 2 || typeof candidate.launchId !== 'string' ||
      !/^[a-f0-9-]{36}$/.test(candidate.launchId) || typeof candidate.startedAt !== 'string' ||
      typeof candidate.voiceDisabled !== 'boolean' || typeof candidate.browser?.userAgent !== 'string' ||
      !Array.isArray(candidate.browser.viewport) || !Array.isArray(candidate.milestones)) return;
    let graphics: LaunchAttempt['graphics'];
    if (candidate.graphics && typeof candidate.graphics.adapterAvailable === 'boolean') {
      const limits: Record<string, number> = {};
      GRAPHICS_LIMITS.forEach(key => {
        const value = candidate.graphics.limits?.[key];
        if (typeof value === 'number' && Number.isFinite(value)) limits[key] = value;
      });
      graphics = { adapterAvailable: candidate.graphics.adapterAvailable, limits,
        features: Array.isArray(candidate.graphics.features) ? candidate.graphics.features
          .filter((v: unknown) => typeof v === 'string').slice(0, 64).map((v: string) => v.slice(0, 128)) : [] };
    }
    const runtime: LaunchAttempt['runtime'] = {};
    RUNTIME_FIELDS.forEach(key => {
      const value = candidate.runtime?.[key];
      if (typeof value === 'string') (runtime as any)[key] = value.slice(0, 64);
      if (typeof value === 'number' && Number.isFinite(value)) (runtime as any)[key] = value;
    });
    // Reconstruct only report fields; never export arbitrary data from storage.
    return {
      schemaVersion: 2,
      launchId: candidate.launchId,
      startedAt: candidate.startedAt.slice(0, 64),
      voiceDisabled: candidate.voiceDisabled,
      navigationType: typeof candidate.navigationType === 'string' ? candidate.navigationType.slice(0, 32) : undefined,
      browser: {
        userAgent: candidate.browser.userAgent.slice(0, 1024),
        viewport: candidate.browser.viewport.slice(0, 2).filter((v: unknown) => typeof v === 'number' && Number.isFinite(v)),
        devicePixelRatio: Number(candidate.browser.devicePixelRatio) || 1,
        secureContext: candidate.browser.secureContext === true,
      },
      ...(graphics ? { graphics } : {}),
      ...(Object.keys(runtime).length ? { runtime } : {}),
      milestones: candidate.milestones.filter((m: any) => m && STAGES.includes(m.stage) &&
        typeof m.elapsedMs === 'number' && Number.isFinite(m.elapsedMs) && Number.isInteger(m.sequence) && m.sequence > 0).slice(-100)
        .map((m: any) => ({ sequence: m.sequence, stage: m.stage, elapsedMs: m.elapsedMs,
          ...safeMemoryCheckpoint(m),
          ...(typeof m.jsHeapBytes === 'number' && Number.isFinite(m.jsHeapBytes) ? { jsHeapBytes: m.jsHeapBytes } : {}) })),
    };
  } catch (_) {
    // Private browsing, denied storage, corrupt data and storage limits cannot block launch.
  }
}

function safeMemoryCheckpoint(value: any): MemoryCheckpoint {
  const result: MemoryCheckpoint = {};
  MEMORY_FIELDS.forEach(key => {
    if (typeof value?.[key] === 'number' && Number.isFinite(value[key]) && value[key] >= 0 && value[key] <= 2 ** 34) result[key] = value[key];
  });
  return result;
}

function saveCheckpoint(): void {
  if (!report) return;
  try {
    // Save one attempt, not its predecessor, so repeated reloads cannot grow storage.
    const { previousLaunch: _, ...current } = report;
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch (_) { /* Best effort. Browser process termination may also discard session storage. */ }
}

/** Local pilot diagnostics only. Do not collect auth, API payloads, URLs or raw errors. */
export function beginMobileWebDiagnostics(voiceDisabled = false): () => void {
  startupModule = undefined;
  window.clearTimeout(startupWatchdog); startupWatchdog = undefined;
  startedAt = performance.now();
  sequence = 0;
  lastFaultTimes.clear();
  const previousLaunch = readPreviousLaunch();
  const navigation = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined;
  const current: LaunchReport = {
    schemaVersion: 2,
    launchId: createLaunchId(),
    startedAt: new Date().toISOString(),
    voiceDisabled,
    ...(navigation ? { navigationType: navigation.type } : {}),
    browser: {
      userAgent: navigator.userAgent,
      viewport: [window.innerWidth, window.innerHeight],
      devicePixelRatio: window.devicePixelRatio,
      secureContext: window.isSecureContext,
    },
    milestones: [],
    ...(previousLaunch ? { previousLaunch } : {}),
  };
  report = current;
  emitCheckpoint();
  const visibility = () => {
    frames = 0; maxGap = 0; lastFrame = performance.now(); sampleStart = lastFrame;
    recordMobileWebStage(document.hidden ? 'hidden' : 'visible');
  };
  const phaseCounts = new Map<string, number>();
  const unityCheckpoint = (event: Event) => {
    const detail = (event as CustomEvent).detail;
    if (!detail || !UNITY_PHASES.includes(detail.phase)) return;
    const count = phaseCounts.get(detail.phase) || 0;
    // Retain bounded room lifecycle samples as well as initial allocation pairs.
    const limit = ZONE_PHASES.some(phase => phase === detail.phase) ? 16 : 2;
    if (count >= limit) return;
    phaseCounts.set(detail.phase, count + 1);
    recordMobileWebStage(detail.phase, safeMemoryCheckpoint(detail));
  };
  window.addEventListener('vv-mobile-startup', unityCheckpoint);
  const pageHide = () => recordMobileWebStage('page-hidden');
  const error = (event: Event) => recordMobileWebStage(event instanceof ErrorEvent ?
    (event.error instanceof WebAssembly.RuntimeError ? 'wasm-error' : 'runtime-error') : 'resource-error');
  const rejection = () => recordMobileWebStage('unhandled-rejection');
  document.addEventListener('visibilitychange', visibility);
  window.addEventListener('pagehide', pageHide);
  window.addEventListener('error', error, true);
  window.addEventListener('unhandledrejection', rejection);

  // Observe the SDK's existing call without requesting permission, retaining the
  // original receiver, arguments, promise identity and returned stream.
  const devices = navigator.mediaDevices;
  const original = devices?.getUserMedia;
  const observed: typeof original = original ? function (...args) {
    if (report === current) recordMobileWebStage('media-requested');
    let pending: Promise<MediaStream>;
    try { pending = original.apply(this, args); }
    catch (error) {
      if (report === current) recordMobileWebStage('media-rejected');
      throw error;
    }
    void pending.then(
      () => { if (report === current) recordMobileWebStage('media-granted'); },
      () => { if (report === current) recordMobileWebStage('media-rejected'); }
    );
    return pending;
  } : undefined;
  try { if (devices && observed) devices.getUserMedia = observed; } catch (_) { /* Some hosts disallow instrumentation. */ }

  // Sample browser scheduling and exposed Wasm/canvas sizes, never total process memory.
  let frames = 0;
  let maxGap = 0;
  let lastFrame = performance.now();
  let sampleStart = lastFrame;
  let frameId = 0;
  let readySamplesRemaining = 0;
  let readySampleAt = 0;
  let lastCanvasSize = '';
  const frame = (now: number) => {
    if (report !== current) return;
    if (!document.hidden) { frames++; maxGap = Math.max(maxGap, now - lastFrame); }
    lastFrame = now;
    if (!document.hidden && (readySamplesRemaining > 0 || !readySampleAt) &&
        current.milestones.some(m => m.stage === 'player-ready')) {
      const canvas = document.getElementById('unity-canvas') as HTMLCanvasElement | null;
      const size = canvas ? `${canvas.width}x${canvas.height}` : '';
      // Only early startup: capture the first actual canvas resize immediately,
      // then exposed heap growth for five seconds, without waiting ten seconds.
      if (!readySampleAt) { readySampleAt = now; readySamplesRemaining = 5; }
      if (readySamplesRemaining > 0 && (size !== lastCanvasSize || now >= readySampleAt)) {
        lastCanvasSize = size;
        readySamplesRemaining--;
        readySampleAt = now + 1000;
        sampleMobileWebRuntime();
        recordMobileWebStage('runtime-sample');
      }
    }
    frameId = window.requestAnimationFrame(frame);
  };
  if (window.requestAnimationFrame) frameId = window.requestAnimationFrame(frame);
  const sampleTimer = window.setInterval(() => {
    const now = performance.now();
    if (!document.hidden && report === current) {
      sampleMobileWebRuntime();
      recordMobileWebRuntime({ browserRafFps: Math.round(frames * 1000 / Math.max(1, now - sampleStart)), maxRafGapMs: Math.round(maxGap) });
      recordMobileWebStage('runtime-sample');
    }
    frames = 0; maxGap = 0; lastFrame = now; sampleStart = now;
  }, 10000);

  return () => {
    window.clearInterval(sampleTimer);
    if (frameId) window.cancelAnimationFrame(frameId);
    stopGpuObserver?.(); stopGpuObserver = undefined;
    document.removeEventListener('visibilitychange', visibility);
    window.removeEventListener('vv-mobile-startup', unityCheckpoint);
    window.removeEventListener('pagehide', pageHide);
    window.removeEventListener('error', error, true);
    window.removeEventListener('unhandledrejection', rejection);
    try {
      if (devices && original && devices.getUserMedia === observed) devices.getUserMedia = original;
    } catch (_) { /* Restore only the observer we own. */ }
    if (report === current) report = undefined;
    if (!report) {
      startupModule = undefined;
      window.clearTimeout(startupWatchdog); startupWatchdog = undefined;
    }
  };
}

export function recordMobileWebStage(stage: Stage, memory: MemoryCheckpoint = {}): void {
  if (!report) return;
  if (stage === 'player-ready' || stage === 'player-failed' || stage === 'unity-abort') {
    window.clearTimeout(startupWatchdog); startupWatchdog = undefined;
  }
  if (stage === 'media-requested') sampleMobileWebRuntime();
  if (stage.startsWith('gpu-') || FAULT_STAGES.has(stage)) {
    const now = performance.now();
    const previous = lastFaultTimes.get(stage);
    if (previous !== undefined && now - previous < 1000) return;
    lastFaultTimes.set(stage, now); // Bound repeated error storms to one checkpoint/second/kind.
  }
  // JS heap is optional and excludes Unity/Wasm, GPU, media and total process memory.
  const heap = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory?.usedJSHeapSize;
  report.milestones.push({ sequence: ++sequence, stage, elapsedMs: Math.round(performance.now() - startedAt),
    ...(typeof heap === 'number' ? { jsHeapBytes: heap } : {}), ...safeMemoryCheckpoint(memory) });
  report.milestones = report.milestones.slice(-100);
  emitCheckpoint();
}

function sampleMobileWebRuntime(): void {
  const module = (window as any).unityInstance?.Module || startupModule;
  const heapBytes = module?.HEAPU8?.buffer?.byteLength ?? module?.HEAPU32?.buffer?.byteLength;
  const canvas = (document.getElementById('unity-canvas') || document.querySelector('canvas')) as HTMLCanvasElement | null;
  recordMobileWebRuntime({ ...(typeof heapBytes === 'number' ? { wasmHeapBytes: heapBytes } : {}),
    ...(canvas ? { canvasWidth: canvas.width, canvasHeight: canvas.height } : {}) });
}

/** Loader-supported callbacks: collect only phases and heap length, never Module contents. */
export function getMobileWebUnityStartupHooks() {
  const current = report;
  if (!current) return {};
  return {
    onRuntimeInitialized: function (this: any) {
      if (report !== current) return;
      startupModule = this;
      sampleMobileWebRuntime();
      recordMobileWebStage('unity-runtime-ready');
    },
    postRun: [function () {
      if (report !== current) return;
      sampleMobileWebRuntime();
      recordMobileWebStage('unity-post-run');
      window.clearTimeout(startupWatchdog);
      startupWatchdog = window.setTimeout(() => {
        if (report === current && !current.milestones.some(m => m.stage === 'player-ready' || m.stage === 'unity-abort' || m.stage === 'player-failed')) {
          sampleMobileWebRuntime();
          recordMobileWebStage('player-init-stalled');
        }
      }, 30000);
    }],
    onAbort: function () {
      if (report === current) recordMobileWebStage('unity-abort');
    },
  };
}

export function recordMobileWebGraphics(adapter: any): void {
  if (!report) return;
  const limits: Record<string, number> = {};
  GRAPHICS_LIMITS.forEach(key => {
    const value = adapter?.limits?.[key];
    if (typeof value === 'number') limits[key] = value;
  });
  report.graphics = { adapterAvailable: !!adapter, limits, features: adapter ? Array.from(adapter.features || []) : [] };
  recordMobileWebStage('probe-complete');
  observeGpuDevices(adapter);
}

export function recordMobileWebRuntime(values: NonNullable<LaunchAttempt['runtime']>): void {
  if (!report) return;
  if (Object.entries(values).every(([key, value]) => (report!.runtime as any)?.[key] === value)) return;
  report.runtime = { ...report.runtime, ...values };
  emitCheckpoint();
}

export function recordMobileWebLaunchSettings(upcUrl: string, quality: string): void {
  if (!report) return;
  const current = report;
  const webBuild = Array.from(document.scripts).map(script => script.src.match(/\/main\.([a-f0-9]+)\.js(?:$|\?)/)?.[1]).find(Boolean);
  recordMobileWebRuntime({ sessionMode: 'online', quality, ...(webBuild ? { webBuild } : {}) });
  try {
    const url = new URL(upcUrl);
    // Fingerprint the artifact address without storing a URL, query, or fragment.
    void crypto.subtle.digest('SHA-256', new TextEncoder().encode(url.origin + url.pathname)).then(digest => {
      if (report !== current) return;
      recordMobileWebRuntime({ upcRef: Array.from(new Uint8Array(digest)).map(v => v.toString(16).padStart(2, '0')).join('').slice(0, 16) });
      recordMobileWebStage('runtime-configured');
    }).catch(() => {});
  } catch (_) { /* Fingerprinting is optional. */ }
}

let stopGpuObserver: (() => void) | undefined;
function observeGpuDevices(adapter: any): void {
  stopGpuObserver?.(); stopGpuObserver = undefined;
  if (!adapter || !report) return;
  const current = report;
  const prototype = Object.getPrototypeOf(adapter);
  const original = prototype?.requestDevice;
  if (typeof original !== 'function') return;
  const removers: (() => void)[] = [];
  const observed = function (this: any, ...args: any[]) {
    if (report === current) recordMobileWebStage('gpu-device-requested');
    const pending = original.apply(this, args);
    void pending.then((device: any) => {
      if (report !== current) return;
      recordMobileWebStage('gpu-device-ready');
      void device.lost?.then((info: any) => { if (report === current) recordMobileWebStage(
        info?.reason === 'destroyed' ? 'gpu-device-destroyed' : 'gpu-device-lost'); });
      const captureGpuError = (error: any, scoped = false) => {
        if (report !== current) return;
        const kind = error?.constructor?.name;
        if (kind === 'GPUValidationError') {
          const category = classifyMobileWebGpuValidation(error?.message);
          recordMobileWebRuntime({ firstGpuValidationCategory: report?.runtime?.firstGpuValidationCategory || category,
            lastGpuValidationCategory: category });
        }
        recordMobileWebStage(kind === 'GPUOutOfMemoryError' ? 'gpu-out-of-memory' :
          kind === 'GPUValidationError' ? (scoped ? 'gpu-scoped-validation-error' : 'gpu-validation-error') : 'gpu-error');
      };
      const gpuError = (event: any) => captureGpuError(event.error);
      device.addEventListener?.('uncapturederror', gpuError);
      removers.push(() => device.removeEventListener?.('uncapturederror', gpuError));
      // Observe Unity's existing error scopes without pushing/popping any additional scopes.
      const pop = device.popErrorScope;
      if (typeof pop === 'function') {
        const own = Object.prototype.hasOwnProperty.call(device, 'popErrorScope');
        const observedPop = function (this: any, ...args: any[]) {
          const pending = pop.apply(this, args);
          void pending.then((error: any) => { if (error) captureGpuError(error, true); }, () => {}).catch(() => {});
          return pending;
        };
        try {
          device.popErrorScope = observedPop;
          removers.push(() => {
            if (device.popErrorScope === observedPop) {
              if (own) device.popErrorScope = pop; else delete device.popErrorScope;
            }
          });
        } catch (_) { /* Instrumentation is best effort on restricted hosts. */ }
      }
    }, () => { if (report === current) recordMobileWebStage('gpu-error'); }).catch(() => {});
    return pending;
  };
  try { prototype.requestDevice = observed; } catch (_) { return; }
  stopGpuObserver = () => {
    try { if (prototype.requestDevice === observed) prototype.requestDevice = original; } catch (_) {}
    removers.forEach(remove => remove());
  };
}

export function getMobileWebReport(): LaunchReport | undefined {
  return report ? JSON.parse(JSON.stringify(report)) : undefined;
}

export function downloadMobileWebReport(): void {
  const snapshot = getMobileWebReport();
  if (!snapshot) return;
  const url = URL.createObjectURL(new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'intersect-mobile-web-report.json';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
